import { computed, onUnmounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import useGet from '@/core/composables/api/useGet'
import { ApiRequestStatus } from '@/core/models/api/api-request-status'
import type { SelfAssessments } from '../models/self-assessments'
import { useLesson } from './use-lesson'

const POLL_INTERVAL_MS = 30_000

export const useSelfAssessments = () => {
  const route = useRoute()
  const { lesson } = useLesson()
  const lessonId = computed(() => route.params.lessonId as string)
  const endpoint = computed(() => `lesson/${lessonId.value}/self-assessments`)

  const { data, status, load, reload } = useGet<SelfAssessments>(endpoint)

  /** Who is asked, and whether the teacher assesses. Updating feedback rewrites both. */
  const askedKey = computed(() => {
    const feedback = lesson.value?.feedback
    if (!feedback) {
      return undefined
    }
    const subjectIds = feedback.subjects.map((subject) => subject.id).join(',')
    return `${subjectIds}:${feedback.teacherAssessment != null}`
  })

  let pollTimer: ReturnType<typeof setInterval> | undefined
  let pollInFlight = false

  const stopPolling = () => {
    if (pollTimer !== undefined) {
      clearInterval(pollTimer)
      pollTimer = undefined
    }
  }

  const poll = async () => {
    if (pollInFlight) {
      return
    }
    pollInFlight = true
    try {
      await reload()
    } finally {
      pollInFlight = false
    }
  }

  const startPolling = () => {
    stopPolling()
    pollTimer = setInterval(() => {
      void poll()
    }, POLL_INTERVAL_MS)
  }

  watch(
    lessonId,
    () => {
      load()
    },
    { immediate: true },
  )

  watch(askedKey, (next, previous) => {
    if (previous === undefined || next === undefined || next === previous) {
      return
    }
    reload()
  })

  watch(
    [lessonId, () => data.value?.lessonId, () => data.value?.endedAt ?? null],
    ([id, loadedLessonId, endedAt]) => {
      stopPolling()
      if (!id || loadedLessonId !== id || endedAt) {
        return
      }
      startPolling()
    },
  )

  onUnmounted(stopPolling)

  /** A failed refresh keeps the page that is already on screen. */
  const visibleStatus = computed(() => {
    if (
      data.value &&
      (status.value === ApiRequestStatus.Error || status.value === ApiRequestStatus.Unavailable)
    ) {
      return ApiRequestStatus.Success
    }
    return status.value
  })

  return { selfAssessments: data, status: visibleStatus, lessonId }
}
